
import React, { useState, useEffect } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Paper, Avatar,
  LinearProgress, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Divider, Alert, Tooltip,
  TextField, ButtonGroup
} from '@mui/material';
import {
  TrendingUp, TrendingDown, LocalPharmacy, Biotech, ShoppingCart,
  HourglassEmpty, CheckCircle, Cancel, AttachMoney, People, Assessment,
  Notifications, ArrowForward, MoreVert, LocalShipping, Warning,
  Refresh, Email, Phone, CalendarToday
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
const Welcomefile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  
  // Date filter state
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customDateMode, setCustomDateMode] = useState(false);
  
  const [dashboardData, setDashboardData] = useState({
    medicineOrders: {
      total: 0, pending: 0, completed: 0, cancelled: 0,
      out_for_delivery: 0, revenue: 0, completion_rate: 0
    },
    diagnosticOrders: {
      total: 0, pending: 0, completed: 0, cancelled: 0,
      report_ready: 0, revenue: 0, completion_rate: 0
    },
    complaints: { total: 0, active: 0, resolved: 0 },
    users: { total: 0, active: 0 },
    recentOrders: [],
    activeUsers: 0,
    totalRevenue: 0,
    revenueGrowth: 0,
    usersGrowth: 0,
    metadata: null
  });

  const dashboardApi = process.env.REACT_APP_DASHBOARD_ANALYTICS_API;

  useEffect(() => {
    // Set today's date as default
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    setStartDate(todayStr);
    setEndDate(todayStr);
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Build query params
      let url = dashboardApi;
      const params = new URLSearchParams();
      
      if (customDateMode && startDate && endDate) {
        params.append('start_date', new Date(startDate).toISOString());
        params.append('end_date', new Date(endDate).toISOString());
      } else {
        params.append('period', selectedPeriod);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await axios.get(url);

      if (response.data.success) {
        setDashboardData(response.data);
      } else {
        setError('Failed to fetch dashboard data');
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    setCustomDateMode(false);
    setTimeout(() => fetchDashboardData(), 100);
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) {
      setCustomDateMode(true);
      fetchDashboardData();
    } else {
      setError('Please select both start and end dates');
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, growth }) => (
    <Card 
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}30`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${color}20`,
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color}>
              {value}
            </Typography>
          </Box>
          <Avatar 
            sx={{ 
              bgcolor: color, 
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
        
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {subtitle}
          </Typography>
        )}
        
        {growth !== undefined && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {growth >= 0 ? (
              <TrendingUp sx={{ fontSize: 18, color: 'success.main' }} />
            ) : (
              <TrendingDown sx={{ fontSize: 18, color: 'error.main' }} />
            )}
            <Typography 
              variant="caption" 
              fontWeight="600"
              color={growth >= 0 ? 'success.main' : 'error.main'}
            >
              {Math.abs(growth).toFixed(1)}% from previous period
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const OrderTypeCard = ({ title, icon, data, color, onViewAll }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 48, height: 48 }}>
            {icon}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight="bold">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Orders: {data.total}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="warning.main">
                {data.pending}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pending
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6}>
            <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="h5" fontWeight="bold" color="success.main">
                {data.completed}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Completed
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Completion Rate
            </Typography>
            <Typography variant="body2" fontWeight="600">
              {data.completion_rate}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={data.completion_rate}
            sx={{ 
              height: 8, 
              borderRadius: 1,
              bgcolor: `${color}15`,
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
              }
            }}
          />
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 1.5,
          bgcolor: 'grey.50',
          borderRadius: 1,
          mb: 2,
        }}>
          <Typography variant="body2" color="text.secondary">
            Total Revenue
          </Typography>
          <Typography variant="h6" fontWeight="bold" color={color}>
            ₹{data.revenue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
          </Typography>
        </Box>

        <Button 
        fullWidth 
        variant="outlined" 
        endIcon={<ArrowForward />}
        onClick={() => navigate("/orders-management")}
        sx={{
          borderColor: color,
          color: color,
          '&:hover': {
            borderColor: color,
            bgcolor: `${color}08`,
          }
        }}
      >
        View All Orders
      </Button>
      </CardContent>
    </Card>
  );

  const getStatusChip = (status, type) => {
    const medicineConfigs = {
      awaiting_bill: { label: 'Awaiting Bill', color: 'warning', icon: <HourglassEmpty sx={{ fontSize: 16 }} /> },
      confirmed: { label: 'Confirmed', color: 'info', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      out_for_delivery: { label: 'Out for Delivery', color: 'secondary', icon: <LocalShipping sx={{ fontSize: 16 }} /> },
      delivered: { label: 'Delivered', color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      cancelled: { label: 'Cancelled', color: 'error', icon: <Cancel sx={{ fontSize: 16 }} /> },
      in_progress: { label: 'In Progress', color: 'info', icon: <HourglassEmpty sx={{ fontSize: 16 }} /> },
    };

    const diagnosticConfigs = {
      new: { label: 'New', color: 'info', icon: <HourglassEmpty sx={{ fontSize: 16 }} /> },
      Confirmed: { label: 'Confirmed', color: 'primary', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      'sample-collected': { label: 'Sample Collected', color: 'info', icon: <Biotech sx={{ fontSize: 16 }} /> },
      'test-in-progress': { label: 'Testing', color: 'warning', icon: <Assessment sx={{ fontSize: 16 }} /> },
      'report-ready': { label: 'Report Ready', color: 'secondary', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      'report-delivered': { label: 'Delivered', color: 'success', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      closed: { label: 'Closed', color: 'default', icon: <CheckCircle sx={{ fontSize: 16 }} /> },
      cancelled: { label: 'Cancelled', color: 'error', icon: <Cancel sx={{ fontSize: 16 }} /> },
    };

    const configs = type === 'Medicine' ? medicineConfigs : diagnosticConfigs;
    const config = configs[status] || { label: status, color: 'default', icon: null };
    
    return (
      <Chip 
        label={config.label} 
        color={config.color} 
        size="small"
        icon={config.icon}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  if (loading && !refreshing) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f7fa' }}>
        <Box sx={{ textAlign: 'center' }}>
          <LinearProgress sx={{ width: 200, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">Loading Dashboard...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', pb: 4,marginLeft:"300px" }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', mb: 3 }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" color="#006D77" gutterBottom>
                Admin Dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {dashboardData.metadata?.displayRange || 'Welcome back!'} • Monitor and manage your operations
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="outlined" 
                startIcon={<Warning />}
                sx={{ 
                  borderColor: '#ff9800',
                  color: '#ff9800',
                  '&:hover': { borderColor: '#f57c00', bgcolor: '#ff980010' },
                }}
              >
                Complaints ({dashboardData.complaints.active})
              </Button>
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={() => fetchDashboardData(true)}
                  disabled={refreshing}
                  sx={{
                    bgcolor: '#006D77',
                    color: '#fff',
                    '&:hover': { bgcolor: '#005761' },
                  }}
                >
                  <Refresh sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl">
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Date Filter Section */}
        <Card sx={{ mb: 3, bgcolor: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CalendarToday sx={{ color: '#006D77' }} />
              <Typography variant="h6" fontWeight="bold">
                Date Filter
              </Typography>
            </Box>

            <Grid container spacing={2} alignItems="center">
              {/* Quick Period Buttons */}
              <Grid item xs={12} md={6}>
                <ButtonGroup variant="outlined" fullWidth>
                  <Button
                    onClick={() => handlePeriodChange('today')}
                    variant={selectedPeriod === 'today' && !customDateMode ? 'contained' : 'outlined'}
                    sx={{
                      bgcolor: selectedPeriod === 'today' && !customDateMode ? '#006D77' : 'transparent',
                      color: selectedPeriod === 'today' && !customDateMode ? 'white' : '#006D77',
                      '&:hover': {
                        bgcolor: selectedPeriod === 'today' && !customDateMode ? '#005761' : '#006D7710',
                      }
                    }}
                  >
                    Today
                  </Button>
                  <Button
                    onClick={() => handlePeriodChange('week')}
                    variant={selectedPeriod === 'week' && !customDateMode ? 'contained' : 'outlined'}
                    sx={{
                      bgcolor: selectedPeriod === 'week' && !customDateMode ? '#006D77' : 'transparent',
                      color: selectedPeriod === 'week' && !customDateMode ? 'white' : '#006D77',
                      '&:hover': {
                        bgcolor: selectedPeriod === 'week' && !customDateMode ? '#005761' : '#006D7710',
                      }
                    }}
                  >
                    Last 7 Days
                  </Button>
                  <Button
                    onClick={() => handlePeriodChange('month')}
                    variant={selectedPeriod === 'month' && !customDateMode ? 'contained' : 'outlined'}
                    sx={{
                      bgcolor: selectedPeriod === 'month' && !customDateMode ? '#006D77' : 'transparent',
                      color: selectedPeriod === 'month' && !customDateMode ? 'white' : '#006D77',
                      '&:hover': {
                        bgcolor: selectedPeriod === 'month' && !customDateMode ? '#005761' : '#006D7710',
                      }
                    }}
                  >
                    This Month
                  </Button>
                  <Button
                    onClick={() => handlePeriodChange('all')}
                    variant={selectedPeriod === 'all' && !customDateMode ? 'contained' : 'outlined'}
                    sx={{
                      bgcolor: selectedPeriod === 'all' && !customDateMode ? '#006D77' : 'transparent',
                      color: selectedPeriod === 'all' && !customDateMode ? 'white' : '#006D77',
                      '&:hover': {
                        bgcolor: selectedPeriod === 'all' && !customDateMode ? '#005761' : '#006D7710',
                      }
                    }}
                  >
                    All Time
                  </Button>
                </ButtonGroup>
              </Grid>

              {/* Custom Date Range */}
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    onClick={handleCustomDateApply}
                    sx={{
                      bgcolor: '#006D77',
                      minWidth: '100px',
                      '&:hover': { bgcolor: '#005761' }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Grid>
            </Grid>

            {customDateMode && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Showing data from {startDate} to {endDate}
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Top KPI Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Orders"
              value={dashboardData.medicineOrders.total + dashboardData.diagnosticOrders.total}
              icon={<ShoppingCart sx={{ fontSize: 28 }} />}
              color="#006D77"
              subtitle="In selected period"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Approvals"
              value={dashboardData.medicineOrders.pending + dashboardData.diagnosticOrders.pending}
              icon={<HourglassEmpty sx={{ fontSize: 28 }} />}
              color="#ff9800"
              subtitle="Requires attention"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Revenue"
              value={`₹${(dashboardData.totalRevenue / 1000).toFixed(1)}K`}
              icon={<AttachMoney sx={{ fontSize: 28 }} />}
              color="#4caf50"
              subtitle="In selected period"
              growth={dashboardData.revenueGrowth}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Active Users"
              value={dashboardData.activeUsers}
              icon={<People sx={{ fontSize: 28 }} />}
              color="#2196f3"
              subtitle="In selected period"
              growth={dashboardData.usersGrowth}
            />
          </Grid>
        </Grid>

        {/* Order Type Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <OrderTypeCard
              title="Medicine Orders"
              icon={<LocalPharmacy sx={{ fontSize: 24 }} />}
              data={dashboardData.medicineOrders}
              color="#006D77"
              onViewAll={() => console.log('View all medicine orders')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <OrderTypeCard
              title="Diagnostic Orders"
              icon={<Biotech sx={{ fontSize: 24 }} />}
              data={dashboardData.diagnosticOrders}
              color="#9c27b0"
              onViewAll={() => console.log('View all diagnostic orders')}
            />
          </Grid>
        </Grid>

        {/* Recent Orders Table */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  Recent Orders
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Latest order activity in selected period
                </Typography>
              </Box>
              <Button 
                variant="text" 
                endIcon={<ArrowForward />}
                sx={{ color: '#006D77' }}
              >
                View All
              </Button>
            </Box>

            {dashboardData.recentOrders.length === 0 ? (
              <Alert severity="info">No orders found in the selected period</Alert>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData.recentOrders.map((order) => (
                      <TableRow 
                        key={order.id}
                        sx={{ 
                          '&:hover': { bgcolor: 'grey.50' },
                          cursor: 'pointer',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" color="primary">
                            {order.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={order.type}
                            size="small"
                            variant="outlined"
                            color={order.type === 'Medicine' ? 'primary' : 'secondary'}
                            icon={order.type === 'Medicine' ? <LocalPharmacy sx={{ fontSize: 16 }} /> : <Biotech sx={{ fontSize: 16 }} />}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{order.customer}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {order.email}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {order.phone}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600">
                            ₹{order.amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getStatusChip(order.status, order.type)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(order.date).toLocaleDateString('en-IN', { 
                              day: '2-digit', 
                              month: 'short', 
                              year: 'numeric' 
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary">
                            <ArrowForward fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Additional Stats */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#ff572215', color: '#ff5722', width: 40, height: 40 }}>
                    <Warning />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {dashboardData.complaints.active}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Complaints
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Total: {dashboardData.complaints.total} | Resolved: {dashboardData.complaints.resolved}
                </Typography>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  color="error"
                  size="small"
                >
                  Review Complaints
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50', width: 40, height: 40 }}>
                    <Assessment />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {dashboardData.medicineOrders.completed + dashboardData.diagnosticOrders.completed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed Orders
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Success rate: {((dashboardData.medicineOrders.completed + dashboardData.diagnosticOrders.completed) / 
                    (dashboardData.medicineOrders.total + dashboardData.diagnosticOrders.total) * 100 || 0).toFixed(1)}%
                </Typography>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  color="success"
                  size="small"
                >
                  View Reports
                </Button>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#f4433615', color: '#f44336', width: 40, height: 40 }}>
                    <Cancel />
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {dashboardData.medicineOrders.cancelled + dashboardData.diagnosticOrders.cancelled}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cancelled Orders
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                  Cancellation rate: {((dashboardData.medicineOrders.cancelled + dashboardData.diagnosticOrders.cancelled) / 
                    (dashboardData.medicineOrders.total + dashboardData.diagnosticOrders.total) * 100 || 0).toFixed(1)}%
                </Typography>
                <Button 
                  fullWidth 
                  variant="outlined" 
                  color="error"
                  size="small"
                >
                  View Details
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

          <style>
         {`
           @keyframes spin {
             from { transform: rotate(0deg); }
             to { transform: rotate(360deg); }
           }
         `}
       </style>
     </Box>
  );
};

export default Welcomefile;